import React, { useEffect, useRef, useState } from 'react';
import { Location, RouteInfo } from '../types/maps';
import { loader } from '../utils/googleMapsLoader';

interface MapComponentProps {
  pickup: Location | null;
  destination: Location | null;
  onRouteUpdate: (routeInfo: RouteInfo) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({ pickup, destination, onRouteUpdate }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsService, setDirectionsService] = useState<google.maps.DirectionsService | null>(null);
  const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    loader.load().then(() => {
      const mapInstance = new google.maps.Map(mapRef.current!, {
        center: { lat: 50.8503, lng: 4.3517 }, // Brussels
        zoom: 12,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      });

      setMap(mapInstance);
      setDirectionsService(new google.maps.DirectionsService());
      const rendererInstance = new google.maps.DirectionsRenderer({
        map: mapInstance,
        suppressMarkers: false,
      });
      setDirectionsRenderer(rendererInstance);
    }).catch((error) => {
      console.error('Error loading Google Maps:', error);
    });
  }, []);

  useEffect(() => {
    if (map && directionsService && directionsRenderer && pickup && destination) {
      const request: google.maps.DirectionsRequest = {
        origin: { lat: pickup.lat, lng: pickup.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: google.maps.TravelMode.DRIVING,
      };

      directionsService.route(request, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          directionsRenderer.setDirections(result);

          const route = result.routes[0];
          if (route && route.legs[0]) {
            const distance = route.legs[0].distance?.value || 0;
            const duration = route.legs[0].duration?.value || 0;

            onRouteUpdate({
              distance: distance / 1000, // Convert to kilometers
              duration: duration / 60, // Convert to minutes
              fare: 0, // Will be calculated by parent component
            });
          }
        }
      });
    }
  }, [pickup, destination, map, directionsService, directionsRenderer]);

  return <div ref={mapRef} className="w-full h-full rounded-lg shadow-lg" />;
};

export default MapComponent;